import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "latitude", "longitude", "locationName"]
  static values = {
    accessToken: String,
    language: { type: String, default: "el" },
    country: { type: String, default: "" },
    proximity: { type: String, default: "" }
  }

  connect() {
    this.debounceTimer = null
    this.selectedResult = null
    
    // Close results when clicking outside
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener('click', this.boundHandleClickOutside)
  }

  // Normalize Greek text for search (remove tonos, normalize sigma, etc.)
  normalizeGreek(text) {
    if (!text) return ""
    
    let normalized = text
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")      // Remove diacritical marks (tonos)
      .replace(/ς/g, "σ")                   // Normalize final sigma
      .replace(/[·]/g, ".")                 // Normalize middle dot
      .replace(/\u037E/g, ";")              // Greek question mark
      .replace(/[""«»]/g, '"')              // Normalize quotes
      .replace(/['']/g, "'")                // Normalize apostrophes
      .replace(/[–—]/g, "-")                // Normalize dashes
      .replace(/[\u200B-\u200D\uFEFF]/g, "")// Remove zero-width spaces
      .replace(/\s+/g, " ")                 // Normalize whitespace
      .trim()
      .toLowerCase()
    
    return normalized
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleClickOutside)
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  search(event) {
    const query = event.target.value.trim()
    
    if (query.length < 2) {
      this.hideResults()
      return
    }

    // Debounce API calls (wait 300ms after user stops typing)
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    
    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query)
    }, 300)
  }

  async fetchResults(query) {
    try {
      // Send the query as-is — Mapbox handles Greek characters (with tonos) correctly
      const searchQuery = query

      const types = 'place,region,district,locality,neighborhood,address' // City and region-level results

      // Use Mapbox v6 API with user's locale first for correct display names
      const primaryLang = this.languageValue || 'en'
      const languages = primaryLang === 'en' ? 'en,el' : `${primaryLang},en`

      let url = `https://api.mapbox.com/search/geocode/v6/forward?` +
        `q=${encodeURIComponent(searchQuery)}&` +
        `access_token=${this.accessTokenValue}&` +
        `types=${types}&` +
        `language=${languages}&` +
        `limit=5`

      // Only restrict to country if set (regional domains)
      if (this.countryValue) {
        url += `&country=${this.countryValue}`
      }

      // Only bias proximity if set (regional domains)
      if (this.proximityValue) {
        url += `&proximity=${this.proximityValue}`
      }

      const response = await fetch(url)
      const data = await response.json()
      
      if (data.features && data.features.length > 0) {
        this.displayResults(data.features)
      } else {
        this.showNoResults()
      }
    } catch (error) {
      console.error('Mapbox API error:', error)
      this.hideResults()
    }
  }

  displayResults(features) {
    // v6 API response format
    this.resultsTarget.innerHTML = features.map(feature => {
      const props = feature.properties || {}
      const coords = feature.geometry?.coordinates || [0, 0]
      const displayName = props.name || props.full_address || ''
      const fullAddress = props.full_address || props.place_formatted || ''
      
      return `
      <div class="px-4 py-3 hover:bg-accent-yellow hover:bg-opacity-20 cursor-pointer transition-colors border-b border-bg-cream last:border-0"
           data-action="click->mapbox-autocomplete#selectResult"
           data-lat="${coords[1]}"
           data-lon="${coords[0]}"
           data-name="${fullAddress}"
           data-feature='${JSON.stringify(feature)}'>
        <div class="flex items-center gap-2">
          <i class="fas fa-map-marker-alt text-primary text-sm"></i>
          <div class="flex-1">
            <div class="text-sm font-medium text-text-primary">
              ${displayName}
            </div>
            <div class="text-xs text-text-primary opacity-60">
              ${props.place_formatted || ''}
            </div>
          </div>
        </div>
      </div>
    `}).join('')
    
    this.showResults()
  }

  showNoResults() {
    this.resultsTarget.innerHTML = `
      <div class="px-4 py-3 text-sm text-text-primary opacity-60 text-center">
        <i class="fas fa-search mr-2"></i>
        No locations found
      </div>
    `
    this.showResults()
  }

  selectResult(event) {
    const element = event.currentTarget
    const lat = element.dataset.lat
    const lon = element.dataset.lon
    const name = element.dataset.name
    const feature = JSON.parse(element.dataset.feature || '{}')
    
    // Store coordinates and name
    this.selectedResult = { lat, lon, name }
    
    // Update input field with location name
    this.inputTarget.value = name
    
    // Store coordinates in hidden fields if they exist (search drawer uses these)
    if (this.hasLatitudeTarget) this.latitudeTarget.value = lat
    if (this.hasLongitudeTarget) this.longitudeTarget.value = lon
    if (this.hasLocationNameTarget) this.locationNameTarget.value = name
    
    // For product forms - populate bilingual city and region fields
    const formContext = this.element.closest('form')
    if (formContext) {
      // Extract bilingual city and region from Mapbox v6 API context with translations
      let cityNameEn = ''
      let cityNameEl = ''
      let regionNameEn = ''
      let regionNameEl = ''
      
      const props = feature.properties || {}
      const context = props.context || {}
      
      // Extract place (city) with translations
      if (context.place) {
        cityNameEn = context.place.name || ''
        cityNameEl = context.place.name || ''
        
        // Check for translations
        if (context.place.translations) {
          if (context.place.translations.en) cityNameEn = context.place.translations.en.name
          if (context.place.translations.el) cityNameEl = context.place.translations.el.name
        }
      } else if (props.name) {
        // If no place context, use feature name
        cityNameEn = props.name
        cityNameEl = props.name
      }
      
      // Extract region with translations
      if (context.region) {
        regionNameEn = context.region.name || ''
        regionNameEl = context.region.name || ''
        
        // Check for translations
        if (context.region.translations) {
          if (context.region.translations.en) regionNameEn = context.region.translations.en.name
          if (context.region.translations.el) regionNameEl = context.region.translations.el.name
        }
      }
      
      // Populate bilingual city fields
      const cityEnField = formContext.querySelector('[name*="location_city_en"]')
      const cityElField = formContext.querySelector('[name*="location_city_el"]')
      if (cityEnField) cityEnField.value = cityNameEn
      if (cityElField) cityElField.value = cityNameEl
      
      // Populate bilingual region fields
      const regionEnField = formContext.querySelector('[name*="location_region_en"]')
      const regionElField = formContext.querySelector('[name*="location_region_el"]')
      if (regionEnField) regionEnField.value = regionNameEn
      if (regionElField) regionElField.value = regionNameEl
      
      // Populate latitude/longitude fields (product forms)
      const latField = formContext.querySelector('[name*="[latitude]"]')
      const lonField = formContext.querySelector('[name*="[longitude]"]')
      if (latField) latField.value = lat
      if (lonField) lonField.value = lon
      
      // Populate primary fields (location, location_city, location_region) - use user's locale
      const locationField = formContext.querySelector('[name*="[location]"]')
      const locationCityField = formContext.querySelector('[name$="[location_city]"]')
      const locationRegionField = formContext.querySelector('[name$="[location_region]"]')
      const useGreek = this.languageValue === 'el'
      if (locationField) locationField.value = name
      if (locationCityField) locationCityField.value = useGreek ? cityNameEl : cityNameEn
      if (locationRegionField) locationRegionField.value = useGreek ? regionNameEl : regionNameEn
    }
    
    // Hide results
    this.hideResults()
    
    // Dispatch custom event with location data
    this.element.dispatchEvent(new CustomEvent('location-selected', {
      detail: { lat, lon, name },
      bubbles: true
    }))
  }

  showResults() {
    this.resultsTarget.classList.remove('hidden')
  }

  hideResults() {
    this.resultsTarget.classList.add('hidden')
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  clear() {
    this.inputTarget.value = ''
    this.selectedResult = null
    if (this.hasLatitudeTarget) this.latitudeTarget.value = ''
    if (this.hasLongitudeTarget) this.longitudeTarget.value = ''
    if (this.hasLocationNameTarget) this.locationNameTarget.value = ''
    this.hideResults()
  }
}
